import {
  Activity,
  AlertCircle,
  ArrowLeft,
  Box,
  Check,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Circle,
  Clock,
  Copy,
  ExternalLink,
  Eye,
  EyeOff,
  GitBranch,
  GitCommit,
  Globe,
  Info,
  Key,
  Loader2,
  MoreVertical,
  Pencil,
  RefreshCw,
  RotateCcw,
  Server,
  Square,
  Trash2,
  Webhook,
  X,
  XCircle,
  Zap,
} from "lucide-react";
import React, { useState, useEffect, useRef } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { DeleteServiceDialog } from "../components/common/DeleteServiceDialog";
import { AutoDeployPanel } from "../components/services/AutoDeployPanel";
import { BackupsPanel } from "../components/services/BackupsPanel";
import { DomainsTab } from "../components/services/DomainsTab";
import { isSensitive } from "../components/services/env-vars/utils";
import { EnvVarsTab } from "../components/services/EnvVarsTab";
import { Alert, AlertDescription } from "../components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Badge } from "../components/ui/badge";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "../components/ui/breadcrumb";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { CopyButton } from "../components/ui/copy-button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { EmptyState } from "../components/ui/empty-state";
import { ErrorState } from "../components/ui/error-state";
import { ExpandableIconButton } from "../components/ui/expandable-icon-button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { PageHeader } from "../components/ui/page-header";
import { Separator } from "../components/ui/separator";
import { Skeleton } from "../components/ui/skeleton";
import { StatusBadge } from "../components/ui/status-badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { api } from "../lib/api";
import { getConnectionSnippets } from "../lib/connectionSnippets";
import { handleError } from "../lib/errors";
import { createLogger } from "../lib/logger";
import { notify } from "../lib/notify";
import { getServiceIcon, isConnectionService } from "../lib/serviceIcons";
import { cn } from "../lib/utils";
import { CustomDomain, Service } from "../types";

const log = createLogger("ServiceDetailsPage");

function TriggerBadge({ trigger }: { trigger?: string }) {
  const map: Record<string, { icon: React.ReactNode; label: string }> = {
    "git-push": { icon: <Webhook className="h-3 w-3" />, label: "git push" },
    manual: { icon: <RefreshCw className="h-3 w-3" />, label: "manual" },
    "env-change": { icon: <Zap className="h-3 w-3" />, label: "env change" },
    initial: { icon: <GitCommit className="h-3 w-3" />, label: "initial" },
  };
  const entry = trigger ? map[trigger] : undefined;
  if (!entry) return null;
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-xs text-muted-foreground">
      {entry.icon}
      {entry.label}
    </span>
  );
}

const ALL_TABS = ["overview", "deployments", "connection", "logs", "domains", "env-vars", "source", "backups"] as const;
type ServiceTabKey = (typeof ALL_TABS)[number];

function formatFramework(fw: string | undefined): string {
  if (!fw) return "Unknown";
  const map: Record<string, string> = {
    // Web frameworks
    react: "React",
    nextjs: "Next.js",
    "next.js": "Next.js",
    nodejs: "Node.js",
    node: "Node.js",
    express: "Express",
    python: "Python",
    fastapi: "FastAPI",
    django: "Django",
    flask: "Flask",
    go: "Go",
    rust: "Rust",
    ruby: "Ruby",
    rails: "Ruby on Rails",
    static: "Static HTML",
    // Catalog services
    postgresql: "PostgreSQL",
    postgres: "PostgreSQL",
    mysql: "MySQL",
    mariadb: "MariaDB",
    mongodb: "MongoDB",
    mongo: "MongoDB",
    redis: "Redis",
    elasticsearch: "Elasticsearch",
    couchdb: "CouchDB",
    influxdb: "InfluxDB",
    timescaledb: "TimescaleDB",
    pgvector: "pgvector",
    neo4j: "Neo4j",
    minio: "MinIO",
    qdrant: "Qdrant",
    cassandra: "Cassandra",
    mlflow: "MLflow",
    openobserve: "OpenObserve",
    langfuse: "Langfuse",
  };
  return map[fw.toLowerCase()] ?? fw.charAt(0).toUpperCase() + fw.slice(1);
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function shortRepoName(url: string): string {
  try {
    const parts = new URL(url).pathname
      .replace(/^\//, "")
      .replace(/\.git$/, "");
    return parts;
  } catch {
    return url;
  }
}

const ServiceDetailsPage: React.FC = () => {
  const { projectId, serviceId } = useParams<{
    projectId: string;
    serviceId: string;
  }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [service, setService] = useState<Service | null>(null);
  const [stackDependencies, setStackDependencies] = useState<Service[]>([]);
  const [projectName, setProjectName] = useState<string>("");
  // projectRole is the caller's role on the parent project. Used to gate
  // write-level controls (redeploy, stop, env vars) for Member/Viewer users.
  // Defaults to "owner" so legacy projects without RBAC records remain fully editable.
  const [projectRole, setProjectRole] = useState<string>("owner");
  const [credentials, setCredentials] = useState<any>(null);
  const [deployments, setDeployments] = useState<any[]>([]);
  const [podLines, setPodLines] = useState<{ ts: string; line: string }[]>([]);
  const [podLogStatus, setPodLogStatus] = useState<"idle" | "connecting" | "live" | "closed">("idle");
  const [loading, setLoading] = useState(true);
  const [loadingCredentials, setLoadingCredentials] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [redeployLoading, setRedeployLoading] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showRedeployDialog, setShowRedeployDialog] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [editNameValue, setEditNameValue] = useState("");
  const [editNameLoading, setEditNameLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [credentialsError, setCredentialsError] = useState<string | null>(null);
  const [maskedCredentials, setMaskedCredentials] = useState<
    Record<string, boolean>
  >({ password: true });
  const [copied, setCopied] = useState(false);
  const [activeSnippet, setActiveSnippet] = useState(0);
  const [showSnippets, setShowSnippets] = useState(true);
  const [customDomains, setCustomDomains] = useState<CustomDomain[]>([]);
  const [deployLogs, setDeployLogs] = useState<any[]>([]);
  const [deployElapsed, setDeployElapsed] = useState(0);
  const deployStartRef = useRef<number | null>(null);
  const logBottomRef = useRef<HTMLDivElement>(null);

  const tabParam = searchParams.get("tab");
  const activeTab = ALL_TABS.includes((tabParam ?? "") as any)
    ? (tabParam as string)
    : "overview";
  const setActiveTab = (value: string) =>
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", value);
      return next;
    });

  useEffect(() => {
    if (!serviceId) return;
    loadServiceData();
    const interval = setInterval(loadServiceData, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceId]);

  useEffect(() => {
    if (!projectId) return;
    api.projects
      .get(projectId)
      .then((data) => {
        setProjectName(data.project?.name ?? "—");
        // Capture the caller's role so we can hide write controls for viewers.
        setProjectRole(data.project?.role ?? "owner");
      })
      .catch((err) => log.warn("Could not load project name", err));
  }, [projectId]);

  // Load sibling dependency services when this is the primary service of a stack.
  useEffect(() => {
    if (!service || !service.stackId || service.stackRole !== "primary" || !projectId) return;
    const stackId = service.stackId;
    api.services
      .list(projectId)
      .then(({ services: all }) => {
        const deps = (all as Service[]).filter(
          (s) => s.stackId === stackId && s.stackRole !== "primary" && s.id !== service.id
        );
        setStackDependencies(deps);
      })
      .catch((err) => log.warn("Could not load stack dependencies", err));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service?.stackId, service?.stackRole]);

  useEffect(() => {
    // Auto-load credentials for connection services (databases, storage, observability)
    if (service && service.deploymentId && !credentials && isConnectionService(service.type)) {
      loadCredentials();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service]);

  useEffect(() => {
    if (activeTab === "connection" && !credentials && service?.deploymentId && isConnectionService(service.type))
      loadCredentials();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Open / close the pod log SSE stream whenever the Logs tab is active and the
  // service is running. Closes automatically when the tab changes or service stops.
  useEffect(() => {
    if (activeTab !== "logs" || !serviceId || service?.status !== "running") {
      return;
    }
    setPodLines([]);
    setPodLogStatus("connecting");
    const es = api.services.streamPodLogs(serviceId, 100);

    es.addEventListener("log", (e: MessageEvent) => {
      try {
        const entry = JSON.parse(e.data) as { ts: string; line: string };
        setPodLogStatus("live");
        setPodLines((prev) => {
          const next = [...prev, entry];
          // Keep at most 500 lines to avoid unbounded memory growth.
          return next.length > 500 ? next.slice(-500) : next;
        });
      } catch {
        // ignore malformed frames
      }
    });

    es.addEventListener("error", () => {
      // EventSource auto-reconnects — just reflect reconnecting state.
      setPodLogStatus((s) => (s === "live" ? "live" : "connecting"));
    });

    return () => {
      es.close();
      setPodLogStatus("closed");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, serviceId, service?.status]);

  // Poll pipeline logs every 2s while a deploy is in progress.
  // The backend appends to pipelineLogs as each stage runs, so polling gives
  // near-real-time build output without requiring a streaming connection.
  useEffect(() => {
    const deploying = service?.status === "building" || service?.status === "pending";
    if (!deploying || !service?.deploymentId) {
      setDeployLogs([]);
      return;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const res: any = await api.deployments.getLogs(service.deploymentId!);
        if (!cancelled) {
          setDeployLogs(res?.logs ?? res?.pipelineLogs ?? (Array.isArray(res) ? res : []));
        }
      } catch {
        // ignore — polling will retry on next tick
      }
    };
    poll();
    const interval = setInterval(poll, 2000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service?.status, service?.deploymentId]);

  // Elapsed timer — counts up from 0 while a deploy is in progress.
  useEffect(() => {
    const deploying = service?.status === "building" || service?.status === "pending";
    if (!deploying) {
      deployStartRef.current = null;
      setDeployElapsed(0);
      return;
    }
    if (!deployStartRef.current) {
      deployStartRef.current = Date.now();
    }
    const t = setInterval(() => {
      setDeployElapsed(Math.floor((Date.now() - (deployStartRef.current ?? Date.now())) / 1000));
    }, 1000);
    return () => clearInterval(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service?.status]);

  // Auto-scroll the log panel to the latest entry whenever logs update.
  useEffect(() => {
    logBottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [deployLogs]);

  const loadServiceData = async () => {
    if (!serviceId) return;
    try {
      if (loading) setError(null);
      // Fetch service and deployments in parallel so there's no intermediate
      // render where the service is set but deployments are still empty.
      const [response, deploymentsData, domainsData] = await Promise.all([
        api.services.get(serviceId),
        api.services.deployments(serviceId).catch((err) => {
          log.warn("Could not load deployments", err);
          return [];
        }),
        api.domains.list(serviceId).catch(() => ({ domains: [] })),
      ]);
      setService(response);
      setDeployments(deploymentsData ?? []);
      setCustomDomains(domainsData?.domains ?? []);
    } catch (err: any) {
      setError(err.message || "Failed to load service details");
    } finally {
      setLoading(false);
    }
  };

  const loadCredentials = async () => {
    if (!serviceId) return;
    try {
      setLoadingCredentials(true);
      setCredentialsError(null);
      const credsData = await api.services.credentials(serviceId);
      setCredentials(credsData);
      if (credsData.connectionConfig?.credentialFields) {
        const maskedState: Record<string, boolean> = {};
        credsData.connectionConfig.credentialFields.forEach((field: any) => {
          if (field.hidden) maskedState[field.key] = true;
        });
        setMaskedCredentials(maskedState);
      } else {
        setMaskedCredentials({ password: true });
      }
    } catch (err: any) {
      setCredentialsError(err.message || "Failed to load credentials");
    } finally {
      setLoadingCredentials(false);
    }
  };

  const handleServiceAction = async (action: "stop" | "restart") => {
    if (!serviceId) return;
    setActionLoading(true);
    try {
      const res: any =
        action === "stop"
          ? await api.services.stop(serviceId)
          : await api.services.restart(serviceId);
      notify.success(res?.message || `Service ${action} requested`);
      await loadServiceData();
    } catch (err) {
      handleError(err, { scope: "ServiceDetailsPage", context: { action } });
    } finally {
      setActionLoading(false);
    }
  };

  const handleRedeploy = async () => {
    if (!serviceId) return;
    setRedeployLoading(true);
    try {
      const res: any = await api.services.redeploy(serviceId);
      notify.success("Redeploy started", { description: "Pulling latest code and rebuilding." });
      if (res?.deploymentId) {
        navigate(`/projects/${projectId}/deployments/${res.deploymentId}`);
      } else {
        await loadServiceData();
      }
    } catch (err) {
      handleError(err, { scope: "ServiceDetailsPage", context: { action: "redeploy" } });
    } finally {
      setRedeployLoading(false);
    }
  };

  const handleSaveName = async () => {
    const trimmed = editNameValue.trim();
    if (!trimmed || trimmed === service?.name) {
      setEditingName(false);
      return;
    }
    setEditNameLoading(true);
    try {
      await api.services.update(serviceId!, { name: trimmed });
      notify.success("Service renamed");
      await loadServiceData();
      setEditingName(false);
    } catch (err) {
      handleError(err, { scope: "ServiceDetailsPage", context: { action: "rename" } });
    } finally {
      setEditNameLoading(false);
    }
  };

  const handleDeleteService = async () => {
    if (!serviceId) return;
    try {
      await api.services.delete(serviceId);
      notify.success("Service deleted");
      setShowDeleteDialog(false);
      navigate(`/projects/${projectId}`);
    } catch (err) {
      handleError(err, { scope: "ServiceDetailsPage" });
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (loading && !service) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-4 w-72" />
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <Skeleton className="h-9 w-64" />
            <Skeleton className="h-4 w-40" />
          </div>
          <Skeleton className="h-9 w-28" />
        </div>
        <Skeleton className="h-40 w-full rounded-lg" />
        <Skeleton className="h-64 w-full rounded-lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-3xl space-y-4 py-6">
        <ErrorState
          title="Couldn't load this service"
          description={error}
          onRetry={loadServiceData}
        />
        <div className="flex justify-center">
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="mx-auto max-w-3xl py-6">
        <EmptyState
          icon={Info}
          title="Service not found"
          description="This service may have been deleted or you don't have access to it."
          primaryAction={{
            label: "Back to project",
            onClick: () => navigate(`/projects/${projectId}`),
          }}
        />
      </div>
    );
  }

  const isRunning = service.status === "running";
  const isStopped = service.status === "stopped";
  const isDatabase = isConnectionService(service.type);

  // Permission gates derived from the project-level role.
  // canWrite: owners and members can trigger deploys, start/stop, edit env vars.
  // isOwner: only owners can delete services.
  const canWrite = projectRole === "owner" || projectRole === "member";
  const isOwner = projectRole === "owner";
  const ServiceIcon = getServiceIcon(service.type);
  const serviceLabel = formatFramework(service.framework || service.type);
  const urlCardLabel = isDatabase ? "Dashboard" : "Live URL";
  const openLabel = isDatabase ? "Open Dashboard" : "Open";
  const latestDeployment = deployments[0] ?? null;
  const latestDeployURL = deployments.find((d) => d.deployURL)?.deployURL;
  // Prefer the platform subdomain (permanent HTTPS URL) over the raw NodePort URL.
  const displayURL =
    (service.appSubdomain ? `https://${service.appSubdomain}` : null) ||
    service.publicUrl ||
    latestDeployURL;
  const isWebService = ["web-frontend", "web-backend", "static", "static-site", "next-js", "containerized", "dockerfile"].includes(service.type);
  const activeDomains = customDomains.filter((d) => d.status === "active");
  const isDeploying = service.status === "building" || service.status === "pending";
  const recentDeployments = deployments.slice(0, 3);

  const visibleTabs = ALL_TABS.filter((t: ServiceTabKey) => {
    if (t === "connection") return isDatabase || isWebService || !!credentials;
    if (t === "domains") return !isDatabase;
    // Env vars are write-level — Viewers cannot see or modify them.
    if (t === "env-vars") return !isDatabase && canWrite;
    if (t === "source") return !isDatabase && !!service.repository?.url;
    if (t === "backups") return canWrite;
    return true;
  });

  return (
    <div className="space-y-6">
      {/* Breadcrumb. Left outside PageHeader because the dependency banner
          below renders between it and the heading. */}
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/projects">Projects</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to={`/projects/${projectId}`}>
                {projectName !== "" && projectName !== "—"
                  ? projectName
                  : <Skeleton className="inline-block h-3.5 w-20 align-middle" />}
              </Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>{service.name}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* Dependency banner — shown when this service is managed by a stack */}
      {service.stackRole === "dependency" && service.stackName && (
        <Alert variant="info">
          <Info className="h-4 w-4" />
          <AlertDescription>
            This service is part of the{" "}
            <strong className="text-foreground">{service.stackName}</strong>{" "}
            stack. It is managed as a dependency and cannot be deleted
            independently. To remove it, delete the primary service of this
            stack.
          </AlertDescription>
        </Alert>
      )}

      {/* Header */}
      <PageHeader
        icon={ServiceIcon}
        badge={!editingName && <StatusBadge kind="service" status={service.status} />}
        meta={
          <span className="text-sm text-muted-foreground">{serviceLabel}</span>
        }
        title={
          <>
            {editingName ? (
              <span className="flex items-center gap-2">
                <Input
                  value={editNameValue}
                  onChange={(e) => setEditNameValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSaveName();
                    if (e.key === "Escape") setEditingName(false);
                  }}
                  autoFocus
                  className="h-9 text-h4 w-56"
                  disabled={editNameLoading}
                />
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 shrink-0"
                  onClick={handleSaveName}
                  disabled={editNameLoading}
                  aria-label="Save name"
                >
                  {editNameLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Check className="h-4 w-4 text-success" />
                  )}
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 shrink-0"
                  onClick={() => setEditingName(false)}
                  disabled={editNameLoading}
                  aria-label="Cancel"
                >
                  <X className="h-4 w-4" />
                </Button>
              </span>
            ) : (
              <span className="flex flex-wrap items-center gap-2.5">
                <span className="truncate">{service.name}</span>
                {canWrite && (
                  <ExpandableIconButton
                    icon={Pencil}
                    label="Rename"
                    variant="ghost"
                    className="h-7 text-muted-foreground hover:text-foreground"
                    onClick={() => {
                      setEditNameValue(service.name);
                      setEditingName(true);
                    }}
                  />
                )}
              </span>
            )}
          </>
        }
        actions={
        <>

          {/* Redeploy is a write action — hidden for Viewers */}
          {canWrite && service.repository?.url && (
            <Button
              size="sm"
              onClick={() => setShowRedeployDialog(true)}
              disabled={redeployLoading}
            >
              {redeployLoading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              Redeploy
            </Button>
          )}

          {/* Actions dropdown is only shown when the caller has at least one
              available action. Viewers have none, so skip the menu entirely. */}
          {(canWrite || isOwner) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  disabled={actionLoading}
                  aria-label="Service actions"
                >
                  {actionLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <MoreVertical className="h-4 w-4" />
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {/* Start / Restart / Stop require member-level access */}
                {canWrite && (
                  <>
                    <DropdownMenuItem
                      onClick={() => handleServiceAction("restart")}
                      disabled={actionLoading}
                    >
                      <RotateCcw className="mr-2 h-4 w-4" />
                      {isStopped ? "Start" : "Restart"}
                    </DropdownMenuItem>
                    {isRunning && (
                      <DropdownMenuItem
                        onClick={() => handleServiceAction("stop")}
                        disabled={actionLoading}
                      >
                        <Square className="mr-2 h-4 w-4" />
                        Stop
                      </DropdownMenuItem>
                    )}
                  </>
                )}
                {/* Delete requires owner access */}
                {isOwner && (
                  <>
                    {canWrite && <DropdownMenuSeparator />}
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive"
                      onClick={() => setShowDeleteDialog(true)}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </>
        }
      />

      <Separator />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList>
          {visibleTabs.map((tab) => (
            <TabsTrigger key={tab} value={tab} className="capitalize">
              {tab === "deployments" && deployments.length > 0 ? (
                <span className="flex items-center gap-1.5">
                  Deployments
                  <Badge
                    variant="secondary"
                    className="rounded-full px-1.5 py-0 text-xs"
                  >
                    {deployments.length}
                  </Badge>
                </span>
              ) : tab === "env-vars" ? (
                "Env Vars"
              ) : tab === "source" ? (
                "CI/CD"
              ) : (
                tab.charAt(0).toUpperCase() + tab.slice(1)
              )}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* ---- Overview ---- */}
        <TabsContent value="overview" className="space-y-6 pt-6">
          {/* Live URL — shown for web services with a URL, or while deploying */}
          {(isWebService || isDatabase) && (displayURL || isDeploying) && (
            <Card>
              <CardContent className="pt-5 pb-5 space-y-3">
                {/* Status indicator row */}
                <div className="flex items-center gap-2">
                  <div
                    className={`h-2 w-2 rounded-full shrink-0 ${
                      isRunning ? "bg-success animate-pulse" :
                      isDeploying ? "bg-warning animate-pulse" :
                      "bg-muted-foreground"
                    }`}
                  />
                  <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">
                    {urlCardLabel}
                  </p>
                </div>

                {/* Primary URL row */}
                {displayURL ? (
                  <div className="flex items-center gap-2 min-w-0">
                    <a
                      href={displayURL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-mono text-sm font-medium hover:underline truncate flex-1 min-w-0"
                    >
                      {displayURL}
                    </a>
                    <div className="flex items-center gap-1 shrink-0">
                      <CopyButton
                        value={displayURL}
                        variant="ghost"
                        expandOnHover
                        successMessage="URL copied"
                      />
                      <ExpandableIconButton
                        icon={ExternalLink}
                        label={openLabel}
                        href={displayURL}
                        variant="ghost"
                      />
                    </div>
                  </div>
                ) : (
                  <span className="text-sm text-muted-foreground italic">
                    Deploy in progress — URL available once running
                  </span>
                )}

                {/* Custom domains — separated by a divider */}
                {activeDomains.length > 0 && (
                  <div className="border-t pt-3 space-y-2">
                    {activeDomains.map((d) => (
                      <div key={d.id} className="flex items-center gap-2 min-w-0">
                        <Globe className="h-3 w-3 text-muted-foreground shrink-0" />
                        <a
                          href={`https://${d.domain}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-mono text-sm text-muted-foreground hover:text-foreground hover:underline truncate flex-1 min-w-0"
                        >
                          {d.domain}
                        </a>
                        <div className="flex items-center gap-1 shrink-0">
                          <CopyButton
                            value={`https://${d.domain}`}
                            variant="ghost"
                            expandOnHover
                            successMessage="Domain URL copied"
                          />
                          <ExpandableIconButton
                            icon={ExternalLink}
                            label="Open"
                            href={`https://${d.domain}`}
                            variant="ghost"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Footer link */}
                {isWebService && displayURL && (
                  <button
                    onClick={() => setActiveTab("domains")}
                    className="block text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
                  >
                    {activeDomains.length > 0 ? "Manage custom domains →" : "Add a custom domain →"}
                  </button>
                )}
              </CardContent>
            </Card>
          )}

          {isDeploying ? (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Deploying
                </CardTitle>
                <span className="text-xs font-mono text-muted-foreground">
                  {String(Math.floor(deployElapsed / 60)).padStart(2, "0")}:{String(deployElapsed % 60).padStart(2, "0")}
                </span>
              </CardHeader>
              <CardContent className="pb-4">
                {(() => {
                  const STAGES = ["prepare", "build", "configure", "deploy", "verify"] as const;
                  type StageStatus = "pending" | "running" | "done" | "error";
                  const status: Record<string, StageStatus> = Object.fromEntries(STAGES.map(s => [s, "pending"]));
                  for (const entry of deployLogs) {
                    const s = entry.stage as string;
                    if (!s || !(s in status)) continue;
                    if (entry.level === "error") status[s] = "error";
                    else if (entry.level === "success" && status[s] !== "error") status[s] = "done";
                    else if (status[s] === "pending") status[s] = "running";
                  }
                  return (
                    <div className="space-y-0.5">
                      {STAGES.map(stage => {
                        const s = status[stage];
                        return (
                          <div key={stage} className="flex items-center gap-3 py-1.5">
                            <div className="w-4 h-4 shrink-0 flex items-center justify-center">
                              {s === "done"    && <CheckCircle className="h-4 w-4 text-success" />}
                              {s === "running" && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
                              {s === "error"   && <XCircle className="h-4 w-4 text-destructive" />}
                              {s === "pending" && <Circle className="h-4 w-4 text-muted-foreground/25" />}
                            </div>
                            <span className={`text-sm capitalize ${
                              s === "running" ? "text-foreground font-medium" :
                              s === "done"    ? "text-foreground" :
                              s === "error"   ? "text-destructive" :
                              "text-muted-foreground/40"
                            }`}>
                              {stage}
                            </span>
                          </div>
                        );
                      })}
                      <div className="pt-3 mt-1 border-t">
                        <button
                          onClick={() => setActiveTab("deployments")}
                          className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
                        >
                          View full logs →
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Only draw the divider when the URL card above was actually rendered */}
              {(isWebService || isDatabase) && !!displayURL && <Separator />}

              {/* Info chips row */}
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4">
                {/* Service type */}
                <div className="rounded-lg bg-card p-3">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                    <Server className="h-3 w-3" />
                    Type
                  </div>
                  <p className="text-sm font-medium capitalize">
                    {service.type?.replace(/-/g, " ") ?? "—"}
                  </p>
                </div>

                {/* Port */}
                {!!service.port && (
                  <div className="rounded-lg bg-card p-3">
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                      <Zap className="h-3 w-3" />
                      Port
                    </div>
                    <p className="text-sm font-medium font-mono">{service.port}</p>
                  </div>
                )}

                {/* Repository */}
                {service.repository?.url && (
                  <div className="rounded-lg bg-card p-3">
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                      <Box className="h-3 w-3" />
                      Repository
                    </div>
                    <a
                      href={service.repository.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium hover:underline truncate block"
                      title={service.repository.url}
                    >
                      {shortRepoName(service.repository.url)}
                    </a>
                  </div>
                )}

                {/* Branch */}
                {(service.repository?.branch || latestDeployment?.branch) && (
                  <div className="rounded-lg bg-card p-3">
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                      <GitBranch className="h-3 w-3" />
                      Branch
                    </div>
                    <p className="text-sm font-medium font-mono">
                      {service.repository?.branch ?? latestDeployment?.branch}
                    </p>
                  </div>
                )}

                {/* Root Directory — only shown for monorepo services */}
                {service.repository?.rootDirectory && (
                  <div className="rounded-lg bg-card p-3">
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                      <Box className="h-3 w-3" />
                      Root Dir
                    </div>
                    <p className="text-sm font-medium font-mono truncate" title={service.repository.rootDirectory}>
                      {service.repository.rootDirectory}
                    </p>
                  </div>
                )}

                {/* Last deployed */}
                <div className="rounded-lg bg-card p-3">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                    <Clock className="h-3 w-3" />
                    Last Deployed
                  </div>
                  <p className="text-sm font-medium">
                    {latestDeployment
                      ? timeAgo(latestDeployment.createdAt)
                      : timeAgo(service.createdAt)}
                  </p>
                </div>


                {/* Auto-deploy */}
                {service.repository?.url && (
                  <div className="rounded-lg bg-card p-3">
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                      <Webhook className="h-3 w-3" />
                      Auto-deploy
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span
                        className={`h-2 w-2 rounded-full shrink-0 ${service.autoDeploy?.enabled ? "bg-success" : "bg-muted-foreground"}`}
                      />
                      <p className="text-sm font-medium">
                        {service.autoDeploy?.enabled
                          ? (service.autoDeploy.branch ?? service.repository.branch ?? "on")
                          : "Off"}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <Separator />

              {/* Recent deployments table */}
              <div>
                <div className="mb-3 flex items-center justify-between gap-2">
                  <h2 className="text-h4">Recent deployments</h2>
                  {deployments.length > 3 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setActiveTab("deployments")}
                    >
                      View all {deployments.length}
                    </Button>
                  )}
                </div>
                {recentDeployments.length === 0 ? (
                  <p className="py-4 text-center text-sm text-muted-foreground">
                    No deployments yet
                  </p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="hover:bg-transparent">
                        <TableHead className="w-[1%] whitespace-nowrap pl-0">
                          Deploy
                        </TableHead>
                        <TableHead>Commit</TableHead>
                        <TableHead className="w-[1%] whitespace-nowrap">
                          Age
                        </TableHead>
                        <TableHead className="w-[1%] whitespace-nowrap pr-0 text-right">
                          Status
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {recentDeployments.map((dep, i) => {
                        const isCurrent = i === 0;
                        const open = () =>
                          navigate(
                            `/projects/${projectId}/deployments/${dep.id}`
                          );
                        return (
                          <TableRow
                            key={dep.id}
                            // A clickable row needs to be reachable and
                            // operable from the keyboard, not just the mouse.
                            role="link"
                            tabIndex={0}
                            aria-label={`Open deployment ${
                              isCurrent
                                ? "current"
                                : `#${deployments.length - i}`
                            }`}
                            className="cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
                            onClick={open}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                open();
                              }
                            }}
                          >
                            <TableCell className="whitespace-nowrap pl-0">
                              <span className="flex items-center gap-2">
                                {/* The live deployment is the one fact worth
                                    ranking in this table — mark it rather than
                                    leaving the reader to infer it from order. */}
                                <span
                                  className={cn(
                                    "h-1.5 w-1.5 shrink-0 rounded-full",
                                    isCurrent ? "bg-primary" : "bg-transparent"
                                  )}
                                  aria-hidden="true"
                                />
                                <span
                                  className={
                                    isCurrent
                                      ? "font-medium text-foreground"
                                      : "text-muted-foreground"
                                  }
                                >
                                  {isCurrent
                                    ? "Current"
                                    : `Deploy #${deployments.length - i}`}
                                </span>
                              </span>
                            </TableCell>

                            <TableCell className="max-w-0">
                              {dep.gitCommit ? (
                                <span className="block min-w-0">
                                  <span className="flex items-center gap-1.5 font-mono text-xs text-muted-foreground">
                                    {dep.branch && (
                                      <>
                                        <span className="truncate">
                                          {dep.branch}
                                        </span>
                                        <span aria-hidden="true">·</span>
                                      </>
                                    )}
                                    <span>{dep.gitCommit.slice(0, 7)}</span>
                                  </span>
                                  {dep.commitMessage && (
                                    // `max-w-0` on the cell lets this truncate
                                    // to whatever width is left, instead of a
                                    // hardcoded 180px that was wrong at every
                                    // other viewport.
                                    <span className="block truncate text-xs text-muted-foreground/70">
                                      {dep.commitMessage.split("\n")[0]}
                                    </span>
                                  )}
                                </span>
                              ) : (
                                "—"
                              )}
                            </TableCell>

                            <TableCell className="whitespace-nowrap tabular-nums text-muted-foreground">
                              {timeAgo(dep.createdAt)}
                            </TableCell>

                            <TableCell className="pr-0 text-right">
                              <StatusBadge
                                kind="deployment"
                                status={dep.status}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </div>

              {/* Env vars — only if present */}
              {service.envVars && Object.keys(service.envVars).length > 0 && (
                <>
                  <Separator />
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base">
                        Environment Variables
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="max-h-48 overflow-y-auto rounded-md bg-muted/30 p-3 font-mono text-xs space-y-1">
                        {Object.entries(service.envVars).map(([key, value]) => (
                          <div key={key} className="flex gap-2">
                            <span className="text-muted-foreground shrink-0">
                              {key}:
                            </span>
                            <span className="break-all">
                              {isSensitive(key) ? "••••••••••••" : (value as string)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </>
              )}

              {/* Stack Components — shown for primary services that have dependency services */}
              {service.stackRole === "primary" && stackDependencies.length > 0 && (
                <>
                  <Separator />
                  <div>
                    <h2 className="mb-3 text-h4">Stack components</h2>
                    <div className="rounded-md bg-card divide-y divide-border">
                      {stackDependencies.map((dep) => {
                        const DepIcon = getServiceIcon(dep.type);
                        return (
                          <div key={dep.id} className="flex items-center gap-3 px-3 py-2.5">
                            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted">
                              <DepIcon className="h-3.5 w-3.5 text-muted-foreground" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium leading-tight truncate">
                                {dep.name}
                              </p>
                              <p className="text-xs text-muted-foreground capitalize">
                                {dep.type?.replace(/-/g, " ") ?? "service"}
                              </p>
                            </div>
                            <StatusBadge kind="service" status={dep.status} />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </TabsContent>

        {/* ---- Deployments ---- */}
        <TabsContent value="deployments" className="pt-6">
          {deployments.length === 0 ? (
            <EmptyState
              icon={Server}
              title="No deployments yet"
              description="Deployments will appear here."
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-[1%] whitespace-nowrap pl-0">
                    Deploy
                  </TableHead>
                  <TableHead>Commit</TableHead>
                  <TableHead className="w-[1%] whitespace-nowrap">Age</TableHead>
                  <TableHead className="w-[1%] whitespace-nowrap">
                    Trigger
                  </TableHead>
                  <TableHead className="w-[1%] whitespace-nowrap pr-0 text-right">
                    Status
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {deployments.map((deployment, i) => {
                  const isCurrent = i === 0;
                  const open = () =>
                    navigate(
                      `/projects/${projectId}/deployments/${deployment.id}`
                    );
                  return (
                    <TableRow
                      key={deployment.id}
                      role="link"
                      tabIndex={0}
                      aria-label={`Open deployment ${
                        isCurrent ? "current" : `#${deployments.length - i}`
                      }`}
                      className="cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
                      onClick={open}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          open();
                        }
                      }}
                    >
                      <TableCell className="whitespace-nowrap pl-0">
                        <span className="flex items-center gap-2">
                          <span
                            className={cn(
                              "h-1.5 w-1.5 shrink-0 rounded-full",
                              isCurrent ? "bg-primary" : "bg-transparent"
                            )}
                            aria-hidden="true"
                          />
                          <span
                            className={
                              isCurrent
                                ? "font-medium text-foreground"
                                : "text-muted-foreground"
                            }
                          >
                            {isCurrent
                              ? "Current"
                              : `Deploy #${deployments.length - i}`}
                          </span>
                        </span>
                        {deployment.deployURL && (
                          <a
                            href={deployment.deployURL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-0.5 inline-flex items-center gap-1 text-xs text-primary hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ExternalLink className="h-3 w-3" />
                            {deployment.deployURL}
                          </a>
                        )}
                      </TableCell>

                      <TableCell className="max-w-0">
                        {deployment.gitCommit ? (
                          <span className="block min-w-0">
                            <span className="flex items-center gap-1.5 font-mono text-xs text-muted-foreground">
                              {deployment.branch && (
                                <>
                                  <span className="truncate">
                                    {deployment.branch}
                                  </span>
                                  <span aria-hidden="true">·</span>
                                </>
                              )}
                              <span>{deployment.gitCommit.slice(0, 7)}</span>
                            </span>
                            {deployment.commitMessage && (
                              <span className="block truncate text-xs text-muted-foreground/70">
                                {deployment.commitMessage.split("\n")[0]}
                              </span>
                            )}
                          </span>
                        ) : (
                          "—"
                        )}
                      </TableCell>

                      {/* Relative age scans far faster than a full locale
                          timestamp, and matches the overview table. The exact
                          time stays available on hover. */}
                      <TableCell
                        className="whitespace-nowrap tabular-nums text-muted-foreground"
                        title={new Date(deployment.createdAt).toLocaleString()}
                      >
                        {timeAgo(deployment.createdAt)}
                      </TableCell>

                      <TableCell className="whitespace-nowrap">
                        <TriggerBadge trigger={deployment.trigger} />
                      </TableCell>

                      <TableCell className="pr-0 text-right">
                        <StatusBadge
                          kind="deployment"
                          status={deployment.status}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </TabsContent>

        {/* ---- Connection ---- */}
        <TabsContent value="connection" className="pt-6 space-y-6">
          {loadingCredentials && !credentials ? (
            <Card>
              <CardContent className="py-10 text-center">
                <Loader2 className="mx-auto h-8 w-8 animate-spin text-muted-foreground" />
                <p className="mt-2 text-sm text-muted-foreground">
                  Loading credentials…
                </p>
              </CardContent>
            </Card>
          ) : credentialsError ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="flex items-center justify-between">
                <span>{credentialsError}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadCredentials}
                  disabled={loadingCredentials}
                >
                  <RefreshCw className="mr-1 h-3 w-3" />
                  Retry
                </Button>
              </AlertDescription>
            </Alert>
          ) : !service.deploymentId ? (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                Service has no active deployment yet.
              </AlertDescription>
            </Alert>
          ) : isWebService ? (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Globe className="h-4 w-4" />
                  Application URL
                </CardTitle>
                <CardDescription>
                  Use this URL to access your app or reference it from other services.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {displayURL ? (
                  <div className="grid grid-cols-[80px_1fr] items-center gap-3">
                    <Label className="text-muted-foreground text-sm text-right">URL</Label>
                    <div className="flex gap-2">
                      <Input
                        value={displayURL}
                        readOnly
                        className="cursor-default bg-muted font-mono text-sm h-9"
                      />
                      <CopyButton
                        value={displayURL}
                        variant="outline"
                        className="h-9 shrink-0"
                        successMessage="Copied"
                      />
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-9 w-9 shrink-0"
                        onClick={() => window.open(displayURL, "_blank")}
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    URL not available yet — the service may still be deploying.
                  </p>
                )}
              </CardContent>
            </Card>
          ) : credentials ? (
            <>
              {/* Credential fields */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Key className="h-4 w-4" />
                    Credentials
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {credentials.connectionConfig?.credentialFields?.length > 0 ? (
                    credentials.connectionConfig.credentialFields.map((field: any) => {
                      const isSecret = field.hidden || field.type === "password" || field.type === "token";
                      const isUrl = field.type === "url";
                      const displayValue = isSecret && maskedCredentials[field.key]
                        ? "••••••••••••"
                        : field.value || "";
                      return (
                        <div key={field.key} className="grid grid-cols-[140px_1fr] items-center gap-3">
                          <Label htmlFor={field.key} className="text-muted-foreground text-sm text-right">
                            {field.label}
                          </Label>
                          <div className="flex gap-2">
                            <Input
                              id={field.key}
                              type="text"
                              value={displayValue}
                              readOnly
                              className="cursor-default bg-muted font-mono text-sm h-9"
                            />
                            {isSecret && (
                              <ExpandableIconButton
                                icon={maskedCredentials[field.key] ? Eye : EyeOff}
                                label={maskedCredentials[field.key] ? "Show" : "Hide"}
                                variant="outline"
                                className="h-9 shrink-0"
                                iconClassName="h-4 w-4"
                                onClick={() =>
                                  setMaskedCredentials((prev) => ({
                                    ...prev,
                                    [field.key]: !prev[field.key],
                                  }))
                                }
                              />
                            )}
                            {isUrl && field.value && (
                              <ExpandableIconButton
                                icon={ExternalLink}
                                label="Open"
                                href={field.value}
                                variant="outline"
                                className="h-9 shrink-0"
                                iconClassName="h-4 w-4"
                              />
                            )}
                            {field.copyable && (
                              <CopyButton
                                value={field.value}
                                variant="outline"
                                expandOnHover
                                className="h-9 shrink-0"
                                successMessage={`${field.label} copied`}
                              />
                            )}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      No credential fields configured for this service.
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Connection strings */}
              {credentials.connectionStrings?.length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Connection Strings</CardTitle>
                    <CardDescription>
                      Use these to connect from your application code or tools
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {credentials.connectionStrings.map((connStr: any, index: number) => (
                      <div key={index} className="space-y-1.5 rounded-md bg-muted/40 p-3">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium">{connStr.name}</p>
                          <Badge variant="outline" className="text-xs font-mono">
                            {connStr.format}
                          </Badge>
                        </div>
                        {connStr.description && (
                          <p className="text-xs text-muted-foreground">{connStr.description}</p>
                        )}
                        <div className="flex gap-2">
                          <Input
                            value={connStr.value}
                            readOnly
                            className="cursor-default bg-background font-mono text-xs h-8"
                          />
                          <CopyButton
                            value={connStr.value}
                            variant="outline"
                            expandOnHover
                            className="h-8 shrink-0"
                            successMessage="Copied"
                          />
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* How to connect — code snippets */}
              {(() => {
                const framework = service.framework || service.type || "";
                const fields = credentials.connectionConfig?.credentialFields || [];
                const snippets = getConnectionSnippets(framework, fields);
                if (!snippets.length) return null;
                return (
                  <Card>
                    <CardHeader
                      className="pb-3 cursor-pointer select-none"
                      onClick={() => setShowSnippets((v) => !v)}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="text-base">How to Connect</CardTitle>
                          <CardDescription>
                            Ready-to-use code snippets for common clients
                          </CardDescription>
                        </div>
                        {showSnippets ? (
                          <ChevronUp className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        )}
                      </div>
                    </CardHeader>
                    {showSnippets && (
                      <CardContent className="pt-0 space-y-3">
                        {/* Tab bar for snippet types */}
                        <div className="flex gap-1 border-b pb-2">
                          {snippets.map((s, i) => (
                            <button
                              key={i}
                              onClick={() => setActiveSnippet(i)}
                              className={`rounded px-3 py-1 text-xs font-medium transition-colors ${
                                activeSnippet === i
                                  ? "bg-primary text-primary-foreground"
                                  : "text-muted-foreground hover:text-foreground"
                              }`}
                            >
                              {s.label}
                            </button>
                          ))}
                        </div>
                        {snippets[activeSnippet] && (
                          <div className="relative">
                            <pre className="overflow-x-auto rounded-md bg-muted p-4 text-xs leading-relaxed font-mono whitespace-pre">
                              {snippets[activeSnippet].code}
                            </pre>
                            <div className="absolute top-2 right-2">
                              <CopyButton
                                value={snippets[activeSnippet].code}
                                variant="outline"
                                expandOnHover
                                className="h-7 bg-background"
                                successMessage="Snippet copied"
                              />
                            </div>
                          </div>
                        )}
                        <p className="text-xs text-muted-foreground">
                          Credentials are pre-filled. Install the client library before running.
                        </p>
                      </CardContent>
                    )}
                  </Card>
                );
              })()}
            </>
          ) : (
            <Card>
              <CardContent className="py-10 text-center">
                <Loader2 className="mx-auto h-8 w-8 animate-spin text-muted-foreground" />
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ---- Domains ---- */}
        <TabsContent value="domains" className="pt-6">
          {serviceId && (
            <DomainsTab
              serviceId={serviceId}
              appSubdomain={service.appSubdomain}
              initialDomains={customDomains}
              onDomainsChange={setCustomDomains}
            />
          )}
        </TabsContent>

        {/* ---- Environment Variables ---- */}
        <TabsContent value="env-vars" className="pt-6">
          {service && (
            <EnvVarsTab
              service={service}
              environmentId={searchParams.get("environmentId") ?? undefined}
              onServiceUpdate={loadServiceData}
            />
          )}
        </TabsContent>

        {/* ---- Source & CI/CD ---- */}
        <TabsContent value="source" className="pt-6 space-y-4">
          {/* Root Directory — only shown for monorepo services that have one set */}
          {service?.repository?.rootDirectory && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <GitBranch className="h-4 w-4" />
                  Monorepo Root Directory
                </CardTitle>
                <CardDescription>
                  Builds run from this subdirectory within the repository.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3 rounded-lg bg-muted/40 px-4 py-3">
                  <span className="font-mono text-sm font-medium flex-1">
                    {service.repository.rootDirectory}
                  </span>
                  <CopyButton
                    value={service.repository.rootDirectory}
                    variant="ghost"
                    successMessage="Copied"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  To change this, update the service via the API or re-create it with a different root directory.
                </p>
              </CardContent>
            </Card>
          )}
          {service && (
            <AutoDeployPanel
              service={service}
              onServiceUpdate={loadServiceData}
            />
          )}
        </TabsContent>

        {/* ---- Logs ---- */}
        <TabsContent value="logs" className="pt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <div>
                <CardTitle>Service Logs</CardTitle>
                <CardDescription>Live stdout/stderr from the running pod</CardDescription>
              </div>
              <div className="flex items-center gap-2 text-xs">
                {podLogStatus === "live" && (
                  <span className="flex items-center gap-1 text-success">
                    <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-success" />
                    Live
                  </span>
                )}
                {podLogStatus === "connecting" && (
                  <span className="flex items-center gap-1 text-warning">
                    <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-warning" />
                    Connecting…
                  </span>
                )}
                {podLogStatus === "closed" && (
                  <span className="text-muted-foreground">Disconnected</span>
                )}
                {podLines.length > 0 && (
                  <span className="text-muted-foreground">{podLines.length} lines</span>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {service?.status !== "running" ? (
                <EmptyState
                  icon={Activity}
                  title={
                    service?.status === "building" || service?.status === "pending"
                      ? "Deploy in progress"
                      : service?.status === "failed"
                      ? "Service failed to start"
                      : "Service is not running"
                  }
                  description={
                    service?.status === "building" || service?.status === "pending"
                      ? "Check the deployment log for build progress."
                      : service?.status === "failed"
                      ? "Check the deployment log to see what went wrong."
                      : "Start the service to see live logs."
                  }
                />
              ) : podLines.length === 0 ? (
                <EmptyState
                  icon={Activity}
                  title={podLogStatus === "connecting" ? "Connecting to pod…" : "No output yet"}
                  description="Logs will appear here as the application writes to stdout/stderr."
                />
              ) : (
                <div className="max-h-[32rem] overflow-auto rounded-md bg-terminal p-4 font-mono text-xs text-terminal-foreground">
                  {podLines.map((entry, i) => (
                    <div key={i} className="mb-0.5 whitespace-pre-wrap">
                      <span className="mr-2 text-muted-foreground opacity-60">
                        {entry.ts.slice(11, 19)}
                      </span>
                      {entry.line}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ---- Backups ---- */}
        <TabsContent value="backups" className="pt-6">
          <BackupsPanel serviceId={serviceId!} serviceType={service.type} />
        </TabsContent>
      </Tabs>

      <AlertDialog open={showRedeployDialog} onOpenChange={setShowRedeployDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Redeploy service?</AlertDialogTitle>
            <AlertDialogDescription>
              This will pull the latest code from{" "}
              <span className="font-medium text-foreground">
                {service.repository?.branch ?? "the configured branch"}
              </span>{" "}
              and rebuild <span className="font-medium text-foreground">{service.name}</span>.
              The current deployment will be replaced once the new build succeeds.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRedeploy}
              disabled={redeployLoading}
            >
              {redeployLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deploying…
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Redeploy
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <DeleteServiceDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        serviceName={service.name}
        onConfirm={handleDeleteService}
      />
    </div>
  );
};

export default ServiceDetailsPage;
